import { useEffect, useState } from "react";
import { QrCode, Copy, Loader2, CheckCircle2 } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const money = (v) => `R$ ${Number(v || 0).toFixed(2).replace(".", ",")}`;

// Fluxo de compra de evento avulso via Pix (Asaas). Só chama onCredited após
// o pagamento ser CONFIRMADO no gateway (crédito concedido no backend).
export default function ExtraEventPix({ fee, onCredited }) {
  const [order, setOrder] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  const paid = (o) => o && (o.collection_status === "PAID" || o.credited);

  const start = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/events/extra/checkout");
      setOrder(data);
      if (paid(data)) { setDone(true); onCredited?.(); }
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const sync = async (silent = false) => {
    if (!order) return;
    if (!silent) setBusy(true);
    try {
      const { data } = await api.post(`/events/extra/${order.id}/sync`);
      setOrder(data);
      if (paid(data)) {
        setDone(true);
        toast.success("Pagamento confirmado! +1 evento liberado.");
        onCredited?.();
      } else if (!silent) {
        toast.info("Pagamento ainda não identificado. Assim que o Pix cair, liberamos automaticamente.");
      }
    } catch (e) { if (!silent) toast.error(apiError(e.response?.data?.detail)); }
    finally { if (!silent) setBusy(false); }
  };

  // auto-verifica a cada 5s enquanto pendente (libera assim que confirmar)
  useEffect(() => {
    if (!order || done || paid(order)) return;
    const t = setInterval(() => sync(true), 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [order?.id, order?.collection_status, done]);

  if (done || paid(order)) {
    return (
      <div className="text-center space-y-2 py-2" data-testid="extra-paid">
        <CheckCircle2 className="h-10 w-10 text-success mx-auto" />
        <p className="font-semibold">Pagamento confirmado! +1 evento liberado.</p>
      </div>
    );
  }

  if (!order) {
    return (
      <Button data-testid="extra-generate-btn" onClick={start} disabled={busy} className="w-full rounded-sm bg-[#FF4D00] hover:bg-[#E64500] gap-2">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <QrCode className="h-4 w-4" />}
        Gerar Pix do evento avulso{fee ? ` (${money(fee)})` : ""}
      </Button>
    );
  }

  return (
    <div className="space-y-3 text-center" data-testid="extra-pix">
      <p className="text-sm text-zinc-400">Pague <span className="text-white font-semibold">{money(order.total)}</span> via Pix para liberar <span className="text-white font-semibold">+1 evento</span>. A criação é liberada só após a confirmação.</p>
      {order.pix?.qr_code_base64 && <img src={`data:image/png;base64,${order.pix.qr_code_base64}`} alt="QR Pix" className="w-48 h-48 mx-auto bg-white p-2 rounded-sm" />}
      {order.pix?.copy_paste && (
        <div className="flex gap-2">
          <input readOnly value={order.pix.copy_paste} className="flex-1 bg-[#09090B] border border-border rounded-sm px-2 h-9 text-xs text-zinc-300" />
          <Button size="sm" variant="outline" className="rounded-sm bg-transparent border-white/15" onClick={() => { navigator.clipboard.writeText(order.pix.copy_paste); toast.success("Código Pix copiado."); }}><Copy className="h-4 w-4" /></Button>
        </div>
      )}
      <div className="flex items-center justify-center gap-2 text-xs text-zinc-500"><Loader2 className="h-3 w-3 animate-spin" />Verificando o pagamento automaticamente…</div>
      <Button data-testid="extra-sync-btn" onClick={() => sync(false)} disabled={busy} variant="outline" className="w-full rounded-sm bg-transparent border-border">Já paguei / Atualizar agora</Button>
    </div>
  );
}
